import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import ReportView from "@/components/solar/ReportView";
import { api, authHeaders, getToken, money, formatApiError } from "@/lib/solarApi";

export default function SolarReport() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      const { data } = await api.get(`/solar/orders/${ref}/report`, { headers: authHeaders() });
      setReport(data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [ref]);

  useEffect(() => {
    if (!getToken()) { navigate("/solar/calculator"); return; }
    if (!ref) { navigate("/solar/dashboard"); return; }
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/solar/orders/${ref}/status`, { headers: authHeaders() });
        if (stopped) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          loadReport();
        } else if (data.payment_status !== "failed" && data.payment_status !== "refunded") {
          setTimeout(poll, 3000);
        }
      } catch (err) {
        if (!stopped) setError(formatApiError(err, "Could not load this order."));
      }
    };
    poll();
    return () => { stopped = true; };
  }, [ref, navigate, loadReport]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/solar/orders/${ref}/pdf`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BNM-Solar-Design-${ref}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Could not download the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Seo title="Your Solar Design Report — BNM Sustainable Energy Limited" description="Your professional BNM solar system recommendation." />
      <section data-testid="solar-report-page" className="bg-mist grid-lines-light min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-24">
          {error && !report && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
              <p className="text-sm text-red-700">{error}</p>
              <Link to="/solar/dashboard" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline">Go to your dashboard</Link>
            </div>
          )}

          {!error && !report && status && status.payment_status !== "paid" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-8 sm:p-10 text-center" data-testid="report-pending">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-amber-600" aria-hidden="true" />
              <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">
                {status.payment_status === "failed" ? "Payment did not complete" : "Awaiting payment confirmation"}
              </h1>
              <p className="mt-3 max-w-lg mx-auto text-sm leading-relaxed text-slate-600">
                {status.payment_status === "failed"
                  ? "Your payment failed or was cancelled. You can try again from your dashboard."
                  : status.instructions || "Your report unlocks automatically once payment is confirmed. This page updates by itself."}
              </p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-slate-500">
                Order {status.reference} · {money(status.amount, status.currency)}
              </p>
              <Link to="/solar/dashboard" data-testid="report-dashboard-link" className="mt-6 inline-block rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-400">
                Go to dashboard
              </Link>
            </div>
          )}

          {!error && !report && !status && (
            <p className="py-24 text-center text-sm text-slate-500">Loading your order…</p>
          )}

          {report && <ReportView calculation={report.calculation} order={report.order} onDownload={downloadPdf} downloading={downloading} />}
        </div>
      </section>
    </>
  );
}
